/**
 * Atribuir o projetista a vários ambientes de uma vez (ex.: o pedido inteiro
 * para a mesma pessoa). Lista as linhas que a tela está mostrando, agrupadas
 * por cliente; marcar o cliente marca todos os ambientes dele.
 */
import React, { useEffect, useMemo, useState } from "react";

import AsyncButton from "../AsyncButton";
import SlideOverPanel from "../SlideOverPanel";
import useProjetistasExecutivo from "../../hooks/useProjetistasExecutivo";
import { isoDoDia, itemDoAmbiente } from "./regras";
import {
  BotaoTexto,
  CabecalhoDaSelecao,
  Campo,
  FormularioDoLote,
  Grupo,
  Marcavel,
  Nota,
} from "./styles";

const RETIRAR = "__retirar__";

/** Só as linhas com identidade confiável; `chave` vira o id da seleção. */
export function linhasAtribuiveis(linhas = []) {
  return linhas
    .filter((linha) => !linha.bloqueioProjetista)
    .map((linha) => ({ linha, item: itemDoAmbiente(linha.rowPlanilha) }))
    .filter(({ item }) => item);
}

export function agruparPorCliente(atribuiveis) {
  const grupos = new Map();
  atribuiveis.forEach((entrada) => {
    const cliente = entrada.linha.cliente || "(sem cliente)";
    if (!grupos.has(cliente)) grupos.set(cliente, []);
    grupos.get(cliente).push(entrada);
  });
  return [...grupos.entries()]
    .map(([cliente, itens]) => ({ cliente, itens }))
    .sort((a, b) => String(a.cliente).localeCompare(String(b.cliente), "pt-BR"));
}

export default function AtribuirEmLote({ aberto, onFechar, linhas }) {
  const { projetistas, atribuir } = useProjetistasExecutivo();
  const [projetistaId, setProjetistaId] = useState("");
  const [inicio, setInicio] = useState(isoDoDia());
  const [marcados, setMarcados] = useState(() => new Set());

  const atribuiveis = useMemo(() => linhasAtribuiveis(linhas), [linhas]);
  const grupos = useMemo(() => agruparPorCliente(atribuiveis), [atribuiveis]);

  useEffect(() => {
    if (!aberto) return;
    setProjetistaId("");
    setInicio(isoDoDia());
    setMarcados(new Set());
  }, [aberto]);

  function alternar(chaves, marcar) {
    setMarcados((anterior) => {
      const proximo = new Set(anterior);
      chaves.forEach((chave) => (marcar ? proximo.add(chave) : proximo.delete(chave)));
      return proximo;
    });
  }

  const todasAsChaves = atribuiveis.map(({ item }) => item.chave);
  const retirar = projetistaId === RETIRAR;
  const pronto = marcados.size > 0 && Boolean(projetistaId) && (retirar || Boolean(inicio));

  async function confirmar() {
    const itens = atribuiveis.filter(({ item }) => marcados.has(item.chave)).map(({ item }) => item);
    await atribuir(itens, retirar ? null : projetistaId, retirar ? null : inicio);
    onFechar();
  }

  const rotuloDoBotao = marcados.size
    ? `${retirar ? "Retirar de" : "Atribuir"} ${marcados.size} ${marcados.size === 1 ? "ambiente" : "ambientes"}`
    : "Escolha os ambientes";

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Atribuir projetista"
      subtitle="Escolha a pessoa, a data de início e os ambientes."
      footer={
        <AsyncButton
          variant="primary"
          onClick={confirmar}
          disabled={!pronto}
          successMessage={retirar ? "Projetista retirado." : "Projetista atribuído."}
        >
          {rotuloDoBotao}
        </AsyncButton>
      }
    >
      <FormularioDoLote>
        <Campo>
          Projetista
          <select value={projetistaId} onChange={(e) => setProjetistaId(e.target.value)}>
            <option value="">Escolher…</option>
            {projetistas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
              </option>
            ))}
            <option value={RETIRAR}>Retirar projetista</option>
          </select>
        </Campo>
        <Campo>
          Início
          <input
            type="date"
            value={retirar ? "" : inicio}
            max={isoDoDia()}
            disabled={retirar}
            onChange={(e) => setInicio(e.target.value)}
          />
        </Campo>
      </FormularioDoLote>
      {!projetistas.length && (
        <Nota>
          Ninguém está na lista de projetistas ainda. Conceda a permissão “Ser projetista do executivo” em Acessos.
        </Nota>
      )}

      <CabecalhoDaSelecao>
        <span>
          {marcados.size} de {todasAsChaves.length} selecionados
        </span>
        <BotaoTexto
          type="button"
          onClick={() => alternar(todasAsChaves, marcados.size !== todasAsChaves.length)}
          disabled={!todasAsChaves.length}
        >
          {marcados.size === todasAsChaves.length && todasAsChaves.length ? "Desmarcar todos" : "Marcar todos"}
        </BotaoTexto>
      </CabecalhoDaSelecao>

      {grupos.map(({ cliente, itens }) => {
        const chaves = itens.map(({ item }) => item.chave);
        const marcadosDoGrupo = chaves.filter((chave) => marcados.has(chave)).length;
        return (
          <Grupo key={cliente}>
            <Marcavel $grupo>
              <input
                type="checkbox"
                checked={marcadosDoGrupo === chaves.length}
                ref={(el) => {
                  if (el) el.indeterminate = marcadosDoGrupo > 0 && marcadosDoGrupo < chaves.length;
                }}
                onChange={(e) => alternar(chaves, e.target.checked)}
              />
              <span className="texto">{cliente}</span>
              <span className="quem">
                {chaves.length} {chaves.length === 1 ? "ambiente" : "ambientes"}
              </span>
            </Marcavel>
            {itens.map(({ linha, item }) => (
              <Marcavel key={item.chave}>
                <input
                  type="checkbox"
                  checked={marcados.has(item.chave)}
                  onChange={(e) => alternar([item.chave], e.target.checked)}
                />
                <span className="texto">{linha.ambiente}</span>
                <span className="quem">{linha.projetista || "sem projetista"}</span>
              </Marcavel>
            ))}
          </Grupo>
        );
      })}
    </SlideOverPanel>
  );
}
